<template>
  <div class="batch-edit-perms-widget">
    <div class="field mb-0">
      <label class="sub-label">Permissions</label>
      <div
        class="buttons has-addons mb-0 perm-buttons"
        :class="{ 'is-warning': v$.selectedPermissions.$invalid }"
      >
        <button
          v-for="perm in POSSIBLE_PERMS"
          :key="perm.label"
          class="button is-small perm-button"
          :class="{
            'is-primary is-selected': selectedPermsVal.has(perm.value),
          }"
          @click="selectPerm(perm)"
        >
          {{ perm.label }}
        </button>
      </div>
    </div>
    <div class="user-group-fields">
      <div class="field mb-0">
        <label class="sub-label">Users</label>
        <SelectContainer
          :value="selectedUsers"
          :label="userChoicesMetaApi.label_field"
          :track-by="userChoicesMetaApi.value_field"
          :load-options="loadUsers"
          placeholder="Select..."
          :multiple="true"
          :class="{ 'is-invalid': needsUserOrGroup }"
          @input="onUsersChange"
        />
      </div>
      <div class="field mb-0">
        <label class="sub-label">Groups</label>
        <SelectContainer
          :value="selectedGroups"
          :label="groupChoicesMetaApi.label_field"
          :track-by="groupChoicesMetaApi.value_field"
          :load-options="loadGroups"
          placeholder="Select..."
          :multiple="true"
          :class="{ 'is-invalid': needsUserOrGroup }"
          @input="onGroupsChange"
        />
      </div>
      <p v-if="needsUserOrGroup" class="help has-text-danger">
        {{ v$.usersOrGroups.atLeastOneSelected.$message }}
      </p>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { get } from '@/api'
import SelectContainer from '@/components/SelectContainer.vue'
import useVuelidate from '@vuelidate/core'
import { required, minLength, helpers } from '@vuelidate/validators'

const POSSIBLE_PERMS = [
  { label: 'View', value: 'view' },
  { label: 'Edit', value: 'change' },
  { label: 'Delete', value: 'delete' },
]

export default {
  name: 'BatchEditPermissions',
  components: { SelectContainer },
  props: {
    permissionsField: { type: Object, required: true },
  },
  emits: ['validate', 'permissions-change', 'users-change', 'groups-change'],
  setup(props, { emit }) {
    const selectedPermissions = ref([])
    const selectedUsers = ref([])
    const selectedGroups = ref([])

    // Derive choices API from the permissionsField prop (no more hardcoded meta)
    const widgetConfig = computed(
      () => props.permissionsField?.widget?.edit?.arguments?.choices,
    )
    const userChoicesMetaApi = computed(() => widgetConfig.value?.users?.api)
    const groupChoicesMetaApi = computed(() => widgetConfig.value?.groups?.api)

    const selectedPermsVal = computed(
      () => new Set(selectedPermissions.value.map((p) => p.value)),
    )

    // Validation
    const atLeastOneSelected = helpers.withMessage(
      'Please select at least one user or group',
      () => selectedUsers.value.length > 0 || selectedGroups.value.length > 0,
    )
    const v$ = useVuelidate(
      {
        selectedPermissions: { required, minLength: minLength(1) },
        usersOrGroups: { atLeastOneSelected },
      },
      {
        selectedPermissions,
        usersOrGroups: computed(() => ({
          selectedUsers: selectedUsers.value,
          selectedGroups: selectedGroups.value,
        })),
      },
    )

    const arePermissionsValid = computed(() => !v$.value.$invalid)
    const needsUserOrGroup = computed(
      () => v$.value.usersOrGroups.atLeastOneSelected.$invalid,
    )

    watch(arePermissionsValid, (valid) => emit('validate', valid))

    // Actions
    function loadUsers() {
      return get(userChoicesMetaApi.value.url)
    }
    function loadGroups() {
      return get(groupChoicesMetaApi.value.url)
    }

    function selectPerm(perm) {
      if (selectedPermsVal.value.has(perm.value)) {
        selectedPermissions.value = selectedPermissions.value.filter(
          (p) => p.value !== perm.value,
        )
      } else {
        selectedPermissions.value.push(perm)
      }
      emit('permissions-change', selectedPermissions.value)
      v$.value.$touch()
    }

    function onUsersChange(val) {
      selectedUsers.value = val ?? []
      emit('users-change', selectedUsers.value)
      v$.value.$touch()
    }
    function onGroupsChange(val) {
      selectedGroups.value = val ?? []
      emit('groups-change', selectedGroups.value)
      v$.value.$touch()
    }

    return {
      v$,
      selectedPermissions,
      selectedUsers,
      selectedGroups,
      selectedPermsVal,
      userChoicesMetaApi,
      groupChoicesMetaApi,
      needsUserOrGroup,
      loadUsers,
      loadGroups,
      selectPerm,
      onUsersChange,
      onGroupsChange,
      POSSIBLE_PERMS,
    }
  },
}
</script>

<style lang="scss" scoped>
.batch-edit-perms-widget {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-2);
  align-items: start;
}

.user-group-fields {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-2);

  > .help {
    grid-column: 1 / -1;
  }
}

.sub-label {
  display: block;
  text-align: left;
  font-size: var(--text-xs);
  font-weight: var(--font-semibold);
  color: var(--text-muted);
  margin-bottom: var(--space-1);
}

@container form-fields (min-width: 640px) {
  .batch-edit-perms-widget {
    grid-template-columns: auto 1fr;
    gap: var(--space-2) var(--space-3);
  }

  .user-group-fields {
    grid-template-columns: 1fr 1fr;
  }
}

.perm-buttons {
  display: flex;
  flex-wrap: nowrap;
}

.perm-button {
  margin: 0;
}

// Error outline that follows .has-addons button border-radius
.buttons.has-addons.is-warning {
  border-radius: var(--bulma-button-border-radius, 4px);
  border: 1px solid var(--danger);
  box-shadow: 0 0 0.0625em 0.1em var(--ring-danger);
}

.is-invalid :deep(.multiselect__tags) {
  border-color: var(--danger);
  box-shadow: 0 0 0.0625em 0.1em var(--ring-danger);
}
</style>
